import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  OfferStructuredFacilityRow,
  offerStructuredFacilityAssociationConfig,
} from '~/components/Offers/configs/structuredFacilityAssociationConfig';

import ReservationService from '~/services/ReservationService';

import { offerQueryKeys } from './useGetOffer';

const APPROVED_FACILITY_LIST_QUERY = {
  status: 'approved' as const,
  limit: '10000',
  page: '1',
};

export const offerStructuredFacilityQueryKeys = {
  associatedFacilities: (offerId: string) => [
    ...offerStructuredFacilityAssociationConfig.associatedItemsQueryKey(offerId),
    { status: 'approved', grouped: true },
  ],
  allAssociatedCodes: (offerId: string) => offerStructuredFacilityAssociationConfig.allAssociatedCodesQueryKey(offerId),
  approvedFacilities: () => [
    ...offerStructuredFacilityAssociationConfig.allItemsQueryKey,
    APPROVED_FACILITY_LIST_QUERY,
  ],
  mappedProperty: (propertyId: string | undefined) => ['reservation', 'property', propertyId] as const,
};

export function useGetMappedProperty(propertyId: string | undefined) {
  return useQuery({
    queryKey: offerStructuredFacilityQueryKeys.mappedProperty(propertyId),
    queryFn: async () => {
      if (!propertyId) {
        throw new Error('Property ID is required');
      }

      const response = await ReservationService.getProperty<App.Property>(propertyId);
      return response.result;
    },
    enabled: !!propertyId,
  });
}

export function useGetOfferStructuredFacilities(offerId: string) {
  return useQuery({
    queryKey: offerStructuredFacilityQueryKeys.associatedFacilities(offerId),
    queryFn: async () => {
      const response = await offerStructuredFacilityAssociationConfig.listAssociatedItems(
        { offerId },
        APPROVED_FACILITY_LIST_QUERY,
      );

      return response.result.items;
    },
  });
}

export function useGetApprovedStructuredFacilities() {
  return useQuery({
    queryKey: offerStructuredFacilityQueryKeys.approvedFacilities(),
    queryFn: async () => {
      const response = await offerStructuredFacilityAssociationConfig.listAllItems(APPROVED_FACILITY_LIST_QUERY);
      return response.result.items;
    },
  });
}

export function useImportOfferStructuredFacilities(offerId: string) {
  return useMutation({
    mutationFn: async () => {
      if (!offerStructuredFacilityAssociationConfig.importItems) {
        throw new Error('Import is not supported for structured facilities');
      }

      const response = await offerStructuredFacilityAssociationConfig.importItems({ offerId });
      return response.result.items;
    },
    retry: false,
  });
}

export function useSaveOfferStructuredFacilities(
  offerId: string,
  { invalidateOfferDetailOnSave = true }: { invalidateOfferDetailOnSave?: boolean } = {},
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: Array<OfferStructuredFacilityRow>) => {
      if (!offerStructuredFacilityAssociationConfig.saveImportedItems) {
        throw new Error('Save is not supported for structured facilities');
      }

      const response = await offerStructuredFacilityAssociationConfig.saveImportedItems({ offerId }, items);
      return response.result.items;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: offerStructuredFacilityAssociationConfig.associatedItemsQueryKey(offerId),
      });
      queryClient.invalidateQueries({ queryKey: offerStructuredFacilityQueryKeys.allAssociatedCodes(offerId) });

      if (invalidateOfferDetailOnSave) {
        queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
      }
    },
    retry: false,
  });
}
